import { buildContentSearchIndex } from '../search/contentSearch';
import type { HitMeta, SearchHit } from '../search/contentSearch';
import type { Bm25Index } from '../search/bm25';
import { withoutGitBusyOverlay } from '../utils/gitBusy';
import { appAlert } from '../utils/appDialog';
import type { AppContext } from './appContext';

export function invalidateSearchIndex(ctx: AppContext): void {
  ctx.searchIndex = null;
  ctx.searchIndexBuild = null;
}

export function ensureSearchIndex(ctx: AppContext): Promise<Bm25Index<HitMeta>> {
  if (ctx.searchIndex) return Promise.resolve(ctx.searchIndex);
  if (!ctx.searchIndexBuild) {
    ctx.searchIndexBuild = buildContentSearchIndex(ctx.github, ctx.markdown, ctx.allPaths)
      .then(idx => {
        ctx.searchIndex = idx;
        return idx;
      })
      .catch(err => {
        ctx.searchIndexBuild = null;
        throw err;
      });
  }
  return ctx.searchIndexBuild;
}

export async function openSearchHit(
  ctx: AppContext,
  hit: SearchHit,
  sidebarEl: HTMLElement,
  mainEl: HTMLElement
): Promise<void> {
    if (ctx.fileView.hasUnsavedChanges()) {
      await appAlert('Save or discard changes before opening a search result.');
      return;
    }
    ctx.revokeDashboardBlob();
    let file = ctx.fileRegistry.files.find(f => f.path === hit.path);
    if (!file) {
      mainEl.innerHTML = `<div class="empty-state"><div class="empty-icon" aria-hidden="true"><span class="app-spinner"></span></div><p>Loading…</p></div>`;
      try {
        const { content, sha } = await withoutGitBusyOverlay(() => ctx.github.readFile(hit.path));
        ctx.fileRegistry.originalContents.set(hit.path, content);
        file = ctx.markdown.parse(hit.path, content, sha);
        ctx.fileRegistry.files.push(file);
      } catch (err) {
        mainEl.innerHTML = `<div class="error-state"><p>⚠ Could not load file: ${(err as Error).message}</p></div>`;
        return;
      }
    } else {
      ctx.markdown.refreshFileFromProse(file);
    }
    ctx.sidebar.setActivePath(hit.path);
    ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
    ctx.fileView.render(mainEl, file);
    requestAnimationFrame(() => {
      ctx.fileView.scrollToSearchTarget(hit.sectionIndex, hit.itemIndex);
    });
}
